import React, { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import JourneyService from "../services/JourneyService";
import { getProblemTitleForLang } from "../journeyProblems";
import { dirForLang, normalizeLang } from "../i18n";
import { getEntryCards, getEntryScreenCopy } from "../journey/entryFlowConfig";
import "./JourneyEntryFlow.css";

export default function JourneyEntryGateway() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const lang = normalizeLang(params.get("lang") || localStorage.getItem("innervoice_lang") || "he");
  const dir = dirForLang(lang);
  const fresh = params.get("fresh") === "1";
  const isLanding = location.pathname === "/journey";
  const copy = useMemo(() => getEntryScreenCopy(lang), [lang]);
  const cards = useMemo(() => getEntryCards(lang), [lang]);
  const resume = useMemo(() => (fresh ? null : JourneyService.getResumeJourney(lang)), [fresh, lang]);
  const resumeProblemTitle = useMemo(
    () => (resume?.problemKey ? getProblemTitleForLang(resume.problemKey, lang, { includeDuration: false }) : ""),
    [lang, resume?.problemKey]
  );

  useEffect(() => {
    try {
      localStorage.setItem("innervoice_lang", lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const appendMobileIfApp = (searchParams) => {
    try {
      if (typeof window !== "undefined" && window.localStorage?.getItem("innervoice_mobile") === "1") {
        searchParams.set("mobile", "1");
      }
    } catch {
      /* ignore */
    }
  };

  const navigateWithLang = (pathname, extra = {}) => {
    const q = new URLSearchParams();
    q.set("lang", lang);
    Object.entries(extra).forEach(([k, v]) => {
      if (v != null && v !== "") q.set(k, String(v));
    });
    appendMobileIfApp(q);
    navigate(`${pathname}?${q.toString()}`);
  };

  const handleCard = (card) => {
    const next = new URLSearchParams();
    next.set("lang", lang);
    next.set("problemKey", card.problemKey);
    next.set("entryKey", card.key);
    appendMobileIfApp(next);
    navigate(`/journey/recognition?${next.toString()}`);
  };

  const openSelection = () => navigateWithLang("/journey/select", { fresh: "1" });
  const openDiscovery = () => navigateWithLang("/journey/discovery");
  const openExplorer = () => navigateWithLang("/journey/explorer");

  if (resume) {
    return (
      <div className="journey-entry-shell" dir={dir}>
        <div className="journey-entry-card">
          <p className="journey-entry-brand">INNERVOICE</p>
          {copy.resumeWelcome ? <p className="journey-entry-resume-welcome">{copy.resumeWelcome}</p> : null}
          <h1 className="journey-entry-resume-day">{copy.dayWaiting(resume.currentDay)}</h1>
          <p className="journey-entry-resume-sub">{copy.waitingLine}</p>
          {copy.resumeEncouragement ? (
            <p className="journey-entry-resume-hug">{copy.resumeEncouragement}</p>
          ) : null}
          {resumeProblemTitle && <p className="journey-entry-resume-problem">{resumeProblemTitle}</p>}

          <div className="journey-entry-actions">
            <button
              type="button"
              className="journey-entry-primary"
              onClick={() =>
                navigate(
                  JourneyService.getReturnTarget(lang, resume.problemKey, {
                    runId: resume.runId,
                  })
                )
              }
            >
              {copy.resume}
            </button>
            <button
              type="button"
              className="journey-entry-secondary"
              onClick={() => {
                const q = new URLSearchParams();
                q.set("lang", lang);
                q.set("fresh", "1");
                appendMobileIfApp(q);
                navigate(`/journey/select?${q.toString()}`);
              }}
            >
              {copy.chooseOther}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLanding) {
    return (
      <div className="journey-entry-shell" dir={dir}>
        <div className="journey-entry-card journey-entry-card-intro">
          <p className="journey-entry-brand">INNERVOICE</p>

          <div className="journey-entry-intro">
            {copy.landingLines.map((line, index) => (
              <p
                key={`${index}-${line}`}
                className={`journey-entry-intro-line ${index === 0 ? "is-primary" : ""}`.trim()}
              >
                {line}
              </p>
            ))}
          </div>

          <div className="journey-entry-actions journey-entry-actions-single">
            <button type="button" className="journey-entry-primary" onClick={openSelection}>
              {copy.landingCta}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="journey-entry-shell" dir={dir}>
      <div className="journey-entry-card">
        <p className="journey-entry-brand">INNERVOICE</p>
        <h1 className="journey-entry-title">{copy.selectionHeadline}</h1>
        {copy.selectionSub ? <p className="journey-entry-subtitle">{copy.selectionSub}</p> : null}

        <p className="journey-entry-section-label">{copy.quickStartLabel}</p>
        <div className="journey-entry-grid">
          {cards.map((card) => (
            <button
              key={card.key}
              type="button"
              className="journey-entry-option"
              onClick={() => handleCard(card)}
            >
              {card.label}
            </button>
          ))}
        </div>

        <div className="journey-entry-more-block">
          <p className="journey-entry-section-label">{copy.moreOptionsLabel}</p>
          <button type="button" className="journey-entry-catalog-btn" onClick={openExplorer}>
            {copy.explorer}
          </button>
          <button type="button" className="journey-entry-guided-link" onClick={openDiscovery}>
            {copy.discovery}
          </button>
        </div>
      </div>
    </div>
  );
}
